import type { Entry } from "contentful";
import { getEntry } from "@/lib/contentful/guards";
import type { TypeBlogArticleSkeleton } from "@/types/contentful/generated";
import { ContentfulHelper } from "@/utils/ContentfulHelper";

type BlogArticleEntry = Entry<TypeBlogArticleSkeleton, undefined, string>;

/** Normalize a Contentful Date (e.g. `2025-12-16`) to a full ISO 8601 datetime with timezone. */
function toIsoDate(value?: string) {
  if (!value) return undefined;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return undefined;
  return date.toISOString();
}

type Options = {
  /** Include the `@context` field so the object is a valid standalone JSON-LD document. */
  includeContext?: boolean;
  /** Prefix for the post `url`, e.g. the site origin. Defaults to a relative `/blog/...` path. */
  baseUrl?: string;
};

export function buildBlogPostingJsonLd(post: BlogArticleEntry, options: Options = {}) {
  const { headline, slug, introSummary, publishedDate, author, featuredImage } = post.fields;

  const resolvedAuthor = getEntry(author);
  const resolvedImage = getEntry(featuredImage);
  const imageData = ContentfulHelper.parseContentfulContentImage(resolvedImage?.fields.media);
  const datePublished = toIsoDate(publishedDate);

  return {
    ...(options.includeContext && { "@context": "https://schema.org" }),
    "@type": "BlogPosting",
    headline,
    ...(introSummary && { description: introSummary }),
    url: `${options.baseUrl ?? ""}/blog/${slug}`,
    ...(datePublished && { datePublished }),
    ...(resolvedAuthor?.fields?.name && { author: { "@type": "Person", name: resolvedAuthor.fields.name } }),
    ...(imageData?.src && { image: imageData.src }),
  };
}
